import { Icon } from '../common/Icon'
import { cn } from '../../lib/cn'
import { accentAlpha } from '../../lib/slug'

interface ProductImageProps {
  src: string | null
  alt: string
  accent?: string
  name?: string
  priority?: boolean
  className?: string
  imgClassName?: string
  sizes?: string
}

/**
 * Product bottle presented on a light "stage". Uses object-contain so packaging is never
 * distorted. When no photo exists (e.g. Hydretain), renders an honest branded placeholder —
 * never a fake bottle.
 */
export function ProductImage({
  src,
  alt,
  accent = '#159A3C',
  name,
  priority = false,
  className,
  imgClassName,
  sizes = '(max-width: 768px) 80vw, 480px',
}: ProductImageProps) {
  if (!src) {
    return (
      <div
        role="img"
        aria-label={`${name ?? 'Product'} — product image coming soon`}
        className={cn('relative flex aspect-square items-center justify-center overflow-hidden rounded-xl', className)}
        style={{ background: `linear-gradient(160deg, ${accentAlpha(accent, 0.16)}, ${accentAlpha(accent, 0.04)})` }}
      >
        <div className="flex flex-col items-center gap-3 px-6 text-center">
          <span
            className="flex h-16 w-16 items-center justify-center rounded-full"
            style={{ background: accentAlpha(accent, 0.16), color: accent }}
          >
            <Icon name="Droplets" className="h-7 w-7" />
          </span>
          {name && <span className="font-display text-lg text-ink">{name}</span>}
          <span className="text-xs font-medium uppercase tracking-[0.18em] text-ink-500">Image coming soon</span>
        </div>
      </div>
    )
  }

  return (
    <div className={cn('relative flex items-center justify-center', className)}>
      <img
        src={src}
        alt={alt}
        width={1150}
        height={1150}
        loading={priority ? 'eager' : 'lazy'}
        // @ts-expect-error fetchpriority is valid HTML but not yet in React types on some versions
        fetchpriority={priority ? 'high' : undefined}
        decoding="async"
        sizes={sizes}
        className={cn(
          'relative z-10 h-full w-full object-contain drop-shadow-[0_24px_40px_rgba(6,48,26,0.14)]',
          imgClassName,
        )}
      />
    </div>
  )
}
